import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgStyle } from '@angular/common';
import { Component, computed, signal, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
    FormBuilder,
    FormControlStatus,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatOption, MatRipple } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatDivider } from '@angular/material/divider';
import { MatError, MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect } from '@angular/material/select';
import { TranslocoDirective } from '@jsverse/transloco';
import { FidelityAvailablePages } from 'app/shared/components/fidelity-base-page/fidelity-base-page.types';
import { catchError, forkJoin, take } from 'rxjs';
import { APP_REGEXES } from '../../../../app.constants';
import { getServerDateFormat } from '../../../../core/dates/date-functions';
import { UserService } from '../../../../core/user/user.service';
import { MAX_LENGTHS as ADDRESS_MAX_LENGTHS } from '../../../../shared/addresses/addresses.consts';
import { FidelityBasePageComponent } from '../../../../shared/components/fidelity-base-page/fidelity-base-page.component';
import { FidelityOverlayPageWrapperComponent } from '../../../../shared/components/fidelity-overlay-page-wrapper/fidelity-overlay-page-wrapper.component';
import { GenericPageLoadingStateComponent } from '../../../../shared/components/generic-page-loading-state/generic-page-loading-state.component';
import { CountriesService } from '../../../../shared/countries/countries.service';
import { CountryForGetCountriesResponse } from '../../../../shared/countries/countries.types';
import { Gender } from '../../../../shared/types/common.types';
import { FidelityPersonalDataService } from '../../personal-data/personal-data.service';
import { BaseDataForUpdatePersonalDto, GetPersonalDataResponse } from '../../personal-data/personal-data.types';

@Component({
    selector: 'app-account-user-info',
    imports: [
        MatLabel,
        ReactiveFormsModule,
        MatFormField,
        MatInput,
        FidelityOverlayPageWrapperComponent,
        TranslocoDirective,
        MatButton,
        MatHint,
        MatError,
        MatDatepickerInput,
        MatDatepicker,
        MatDatepickerToggle,
        MatSuffix,
        MatOption,
        MatSelect,
        NgStyle,
        MatRipple,
        MatDivider,
        MatAutocomplete,
        MatAutocompleteTrigger,
        FormsModule,
        MatProgressSpinner,
        GenericPageLoadingStateComponent,
        CdkScrollable,
        MatIcon,
        MatIconButton,
    ],
    templateUrl: './account-user-info-page.component.html',
    styleUrl: './account-user-info-page.component.scss',
})
export class AccountUserInfoPageComponent extends FidelityBasePageComponent {
    availablePages: FidelityAvailablePages = {};
    originalData: GetPersonalDataResponse;
    customerForm: FormGroup;
    customerFormStatus: Signal<FormControlStatus | undefined>;
    protected disableCrudActions: Signal<boolean> = this.personalDataService.disableCrudActions;
    protected fetchingData: Signal<boolean> = this.personalDataService.fetchingData;

    protected disableSave = computed<boolean>(() => {
        this.customerFormStatus();
        return this.customerForm.invalid || this.disableCrudActions();
    });

    availableCountries = signal<CountryForGetCountriesResponse[]>([]);
    filterText = signal<string>('');
    filteredCountries = computed<CountryForGetCountriesResponse[]>(() =>
        this.availableCountries().filter((x) => x.name?.toLowerCase().includes(this.filterText()?.toLowerCase()))
    );

    protected readonly ADDRESS_MAX_LENGTHS = ADDRESS_MAX_LENGTHS;
    protected readonly Gender = Gender;

    constructor(
        private fb: FormBuilder,
        private personalDataService: FidelityPersonalDataService,
        private countriesService: CountriesService,
        private userService: UserService
    ) {
        super();

        this.customerForm = this.fb.group({
            // Non-editable fields
            firstName: [''],
            lastName: [''],
            contactEmail: [''],
            // Editable fields
            phoneNumber: [
                '',
                [
                    Validators.pattern(APP_REGEXES.PHONE_NUMBER),
                    Validators.maxLength(ADDRESS_MAX_LENGTHS.PHONE_NUMBER_A),
                ],
            ],
            gender: [Gender.unspecified, [Validators.required]],
            birthDate: [null],
            street: [null, [Validators.maxLength(ADDRESS_MAX_LENGTHS.STREET)]],
            postalCode: [null, [Validators.required, Validators.maxLength(ADDRESS_MAX_LENGTHS.POSTAL_CODE)]],
            city: [null, [Validators.required, Validators.maxLength(ADDRESS_MAX_LENGTHS.CITY)]],
            province: [null, [Validators.maxLength(ADDRESS_MAX_LENGTHS.PROVINCE)]],
            country: [null],
        });
        this.customerFormStatus = toSignal(this.customerForm.statusChanges);

        // Use take(1) to get the current user value and complete the observable.
        // ReplaySubjects do not complete on their own and this would prevent forkJoin from completing,
        // but we only need the current user here anyway so take(1) is fine.
        const userObservable = this.userService.user$.pipe(take(1));
        const loadTranslationScopeObservable = this.translocoService.load(
            'countries/' + this.translocoService.getActiveLang()
        );
        const loadCountriesObservable = this.countriesService.getCountries();
        const loadPersonalDataObservable = this.personalDataService.getPersonalData();

        forkJoin({
            user: userObservable,
            translations: loadTranslationScopeObservable,
            countries: loadCountriesObservable,
            personalData: loadPersonalDataObservable,
        })
            .pipe(takeUntilDestroyed(), catchError(this.handlePageError.bind(this)))
            .subscribe(({ user, countries, personalData }) => {
                this.availableCountries.set(
                    countries.result.countries.map((x) => {
                        x.name = this.translocoService.translate('countries.' + x.alpha2);
                        return x;
                    })
                );

                this.originalData = personalData.result;
                this.customerForm.patchValue({
                    firstName: personalData.result.firstName,
                    lastName: personalData.result.lastName,
                    contactEmail: personalData.result.contactEmail ?? user.email,
                    birthDate: personalData.result.birthDate,
                    gender: personalData.result.gender,
                    phoneNumber: personalData.result.phoneNumber,
                    street: personalData.result.address?.street,
                    city: personalData.result.address?.city,
                    province: personalData.result.address?.province,
                    country: {
                        ...personalData.result.address?.country,
                        name: this.translocoService.translate(
                            'countries.' + personalData.result.address?.country.alpha2
                        ),
                    },
                    postalCode: personalData.result.address?.postalCode,
                });
            });
    }

    saveChanges() {
        let dto: BaseDataForUpdatePersonalDto = {
            gender: this.customerForm.value.gender,
            phoneNumber: this.customerForm.value.phoneNumber,
        };
        dto.birthDate = getServerDateFormat(this.customerForm.value.birthDate);
        dto.address = {
            street: this.customerForm.value.street,
            city: this.customerForm.value.city,
            province: this.customerForm.value.province,
            postalCode: this.customerForm.value.postalCode,
            countryId: this.customerForm.value.country?.id,
        };

        this.personalDataService
            .updatePersonalData(dto)
            .pipe(takeUntilDestroyed(this.destroyRef), catchError(this.handlePageError.bind(this)))
            .subscribe((_) => {
                this.notifyResult(
                    this.translocoService.translate('fidelity-app.account.user-info.notification-caption')
                );
                this.fidelityAppNavService.back();
            });
    }

    filterCountries($event: any) {
        this.filterText.set($event.target.value);
    }

    resetFilter() {
        this.filterText.set('');
    }

    setCountry($event: MatAutocompleteSelectedEvent) {
        this.customerForm.get('country').patchValue($event.option.value);
        this.resetFilter();
    }

    getOptionName = (option: CountryForGetCountriesResponse) => option.name;
}
